/* eslint-disable indent */

import React, { ReactNode, useEffect, useState } from 'react';
import { EpButton } from '../EpButton';
import { EpIcon } from '../EpIcon';

import './EpCard.scss';

interface EpCardProps {
  cardId: string;
  cardHasHeader?: boolean;
  cardHeaderId?: string;
  cardHeaderTitle?: string;
  cardHeaderIconName?: string;
  cardHasCloseButton?: boolean;
  cardElevation: 'one' | 'two';
  cardSpace?: 'lg' | 'xl';
  cardHeight: number | 'auto' | 'self';
  cardWidth: number | 'auto';
  cardBodyContentPadding?: string | 'lg' | 'xl';
  cardBodyContentWidthType?: 'contained' | 'leaked';
  customClasses?: string;
  children: ReactNode;
}

export const EpCard: React.FC<EpCardProps> = ({
  cardId,
  cardHasHeader = false,
  cardHeaderId,
  cardHeaderTitle = '',
  cardHeaderIconName = '',
  cardHasCloseButton = false,
  cardElevation,
  cardSpace = 'lg',
  cardHeight,
  cardWidth,
  cardBodyContentPadding = '',
  cardBodyContentWidthType = 'contained',
  customClasses,
  children,
}) => {
  const [cardBodyHeight, setCardBodyHeight] = useState('0px');
  const cardSuffixId = typeof document !== 'undefined' ? document.querySelectorAll('.ep-card').length : <div></div>;

  const handleCloseCard = (cardId: string): void => {
    const card = document.getElementById(cardId);
    if (card) {
      card.classList.add('ep-card--hidden');
    }
  };

  const calculateCardBodyHeight = (): string => {
    const card = document.querySelector(`#${cardId}`) as HTMLDivElement;
    const cardMeasureObj = card.getBoundingClientRect();
    const cardHeight = cardMeasureObj.height;

    if (!cardHasHeader) {
      return `${cardHeight.toString()}px`;
    }

    const cardHeader: Element | null =
      document.querySelector(`#${cardHeaderId}`) || null;

    if (cardHeader === null) return '0';

    const cardHeaderHeight = Number(
      window.getComputedStyle(cardHeader).marginBottom.split('px')[0],
    );
    const calculateCardBodyHeight = `${cardHeight - cardHeaderHeight}px`;

    return calculateCardBodyHeight.toString();
  };

  const setDefaultCardContentPadding = (
    cardBodyContentPadding: string | 'lg' | 'xl',
    cardSpace: 'lg' | 'xl',
  ): string => {
    const cardContentPaddingLg = '0 32px 16px 4px';
    const cardContentPaddingXl = '0 48px 16px 4px';

    if (cardBodyContentPadding === '') {
      return cardSpace === 'lg' ? cardContentPaddingLg : cardContentPaddingXl;
    }

    if (cardBodyContentPadding === 'lg') {
      return cardContentPaddingLg;
    }

    if (cardBodyContentPadding === 'xl') {
      return cardContentPaddingXl;
    }

    return cardBodyContentPadding.toString();
  };

  const setCardHeight = (cardHeight: number | 'auto' | 'self'): string => {
    if (cardHeight === 'auto') {
      return '100%';
    } else if (cardHeight === 'self') {
      return 'auto';
    } else {
      return `${cardHeight}px`.toString();
    }
  };

  useEffect(() => {
    setCardBodyHeight(calculateCardBodyHeight());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardHasHeader, cardId]);

  return (
    <div
      id={cardId}
      className={`ep-card ep-card--elevation-${cardElevation} ep-card--${cardSpace} ep-card--${cardBodyContentWidthType} ${!cardHasHeader ? 'ep-card--without-header' : ''
        } ${customClasses ? customClasses : ''}`}
      style={{
        width: `${cardWidth === 'auto' ? '100%' : cardWidth + 'px'}`,
        height: setCardHeight(cardHeight),
      }}
    >
      {cardHasHeader && (
        <div
          {...(cardHasHeader && { id: cardHeaderId })}
          className="ep-card__header"
        >
          {cardHeaderIconName !== '' && (
            <div className="ep-card__header-icon">
              <EpIcon iconName={cardHeaderIconName} iconSize={48} />
            </div>
          )}
          {cardHeaderTitle !== '' && (
            <h2 className="ep-card__header-title">{cardHeaderTitle}</h2>
          )}
          {cardHasCloseButton && (
            <div className="ep-card__header-button--close">
              <EpButton
                buttonId={`ep-card__header-button--close-${cardSuffixId}`}
                buttonType="link-secondary"
                buttonSize="md"
                buttonLeftIconName="SolidTimes"
                buttonOnClickFunc={() => handleCloseCard(cardId)}
              ></EpButton>
            </div>
          )}
        </div>
      )}
      <div
        id={`ep-card__body--${cardSuffixId}`}
        className="ep-card__body"
        style={{
          maxHeight: `${cardHeight === 'self' ? 'none' : cardBodyHeight}`,
          padding: setDefaultCardContentPadding(
            cardBodyContentPadding,
            cardSpace,
          ),
        }}
      >
        <div className="ep-card__body-wrapper">{children}</div>
      </div>
    </div>
  );
};
